import { DOCUMENT, inject, Injectable, Renderer2, signal } from "@angular/core";

/**
 * Servicio encargado de gestionar el tema visual de la aplicación (modo claro/oscuro).
 */
@Injectable()
export class ThemeService {
  private _document = inject(DOCUMENT);
  private _render = inject(Renderer2);

  /**
   * Signal que indica si el modo oscuro está activo.
   */
  darkMode = signal<boolean | null>(null);

  /**
   * Inicializa el servicio recuperando la preferencia guardada en localStorage.
   * Por defecto establece el modo claro si no hay registros previos.
   */
  constructor() {
    const currentTheme = localStorage.getItem('darkMode');

    if (currentTheme) {
      this.darkMode.set(JSON.parse(currentTheme).darkMode);
    } else {
      const theme = { darkMode: false };
      localStorage.setItem('darkMode', JSON.stringify(theme));
      this.darkMode.set(theme.darkMode);
    }

    this.setTheme();
  }

  /**
   * Alterna entre el tema claro y oscuro.
   * Utiliza la API de View Transitions para una transición visual fluida.
   */
  toggleTheme(): void {
    (document as any).startViewTransition(() => {
      this.darkMode.set(!this.darkMode());
      const theme = { darkMode: this.darkMode() };
      const themeActive = this.darkMode() ? 'dark' : 'light';

      this._document.body.style.setProperty('color-scheme', themeActive);
      this.darkMode()
        ? this._render.addClass(this._document.body, 'dark-theme')
        : this._render.removeClass(this._document.body, 'dark-theme');
      localStorage.setItem('darkMode', JSON.stringify(theme));
    });
  }

  /**
   * Sincroniza el estado actual del tema con el DOM.
   * Configura la propiedad 'color-scheme' y gestiona la clase CSS 'dark-theme' en el body.
   */
  setTheme(): void {
    const isDark = this.darkMode();
    const themeActive = isDark ? 'dark' : 'light';
    this._document.body.style.setProperty('color-scheme', themeActive);

    isDark
      ? this._render.addClass(this._document.body, 'dark-theme')
      : this._render.removeClass(this._document.body, 'dark-theme');
  }
}
